import { DateInput, TextInput } from '@core/components/Inputs';
import CurrencyTextField from '@core/components/Inputs/CurrencyTextField';
import { SelectInput } from '@core/components/Select';
import { useApiUtils } from '@core/util/apiUtils';
import { Grid, Tooltip } from '@material-ui/core';
import { Help } from '@material-ui/icons';
import SelectBanco from 'app/components/Select/SelectBanco';
import empresaOnboardingApi from 'app/services/api/empresaOnboardngApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { useFormik } from 'formik';
import { default as React, useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { OnboardingContext } from '../OnboardingContext';
import OnboardingLayout from '../OnboardingLayout';
import { useOnboardingSchemaContaBancaria } from '../schemas/OnboardingSchemas';
import InformativoLateral from './InformativoLateral';

const ContaBancariaOnboarding = () => {
  const [empresaAtual] = useContext(GlobalContext).empresa;
  const [, setLoading] = useContext(OnboardingContext).loading;
  const [conta, setConta] = useContext(OnboardingContext).conta;
  const [etapa] = useContext(OnboardingContext).etapa;
  const [, setAtualizarEtapa] = useContext(OnboardingContext).atualizarEtapa;
  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const onlyNumberRegex = /^\d*$/;
  const alphaNumericRegex = /^[a-z0-9]+$/i;

  const initialValues = {
    pessoa: {
      conta: {
        id: conta?.id,
        descricao: conta?.descricao,
        contaBancaria: {
          id: conta?.contaBancariaId,
          tipoConta: conta?.tipo,
          banco: conta?.banco || null,
          agencia: conta?.agencia,
          agenciaDigito: conta?.agenciaDigito,
          numeroConta: conta?.numeroConta,
          contaDigito: conta?.contaDigito,
          saldoInicial: conta?.saldoInicial,
          dataSaldo: conta?.dataSaldo,
          principal: true,
        },
      },
    },
  };

  const formik = useFormik({
    validationSchema: useOnboardingSchemaContaBancaria(),
    initialValues,
    onSubmit: () => null,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const desabilitarAgenciaConta = !formik?.values?.pessoa?.conta?.contaBancaria?.banco;

  useEffect(() => {
    formik.resetForm(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conta]);

  const carregaConta = async () => {
    if (etapa === 'CONTA_BANCARIA') {
      const resp = await empresaOnboardingApi.findConta(empresaAtual.id);
      setConta(resp.data || {});
    }
  };

  useEffect(() => {
    carregaConta();
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeBanco = (event) => {
    formik.handleChange(event);
    formik.handleChange({ target: { id: 'pessoa.conta.contaBancaria.agencia', value: '' } });
    formik.handleChange({ target: { id: 'pessoa.conta.contaBancaria.agenciaDigito', value: '' } });
    formik.handleChange({ target: { id: 'pessoa.conta.contaBancaria.numeroConta', value: '' } });
    formik.handleChange({ target: { id: 'pessoa.conta.contaBancaria.contaDigito', value: '' } });
  };

  const isBancoBrasil = formik.values?.pessoa?.conta?.contaBancaria?.banco?.codigo === '001';
  const isBradesco = formik.values?.pessoa?.conta?.contaBancaria?.banco?.codigo === '237';

  const _errors = {
    pessoa: {
      conta: {
        descricao:
          (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.descricao) &&
          formik.errors?.pessoa?.conta?.descricao,
        contaBancaria: {
          banco:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.banco) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.banco,
          tipoConta:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.tipoConta) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.tipoConta,
          agencia:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.agencia) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.agencia,
          agenciaDigito:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.agenciaDigito) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.agenciaDigito,
          numeroConta:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.numeroConta) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.numeroConta,
          contaDigito:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.contaDigito) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.contaDigito,
          saldoInicial:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.saldoInicial) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.saldoInicial,
          dataSaldo:
            (formik.submitCount > 0 || formik.touched?.pessoa?.conta?.contaBancaria?.dataSaldo) &&
            formik.errors?.pessoa?.conta?.contaBancaria?.dataSaldo,
        },
      },
    },
  };

  const handleSubmit = async () => {
    formik.submitForm();

    if (!formik.isValid) {
      return;
    }

    const numeroConta = formik.values?.pessoa?.conta?.contaBancaria?.numeroConta;

    if (!!numeroConta) {
      setLoading(true);
      const objectRequest = { empresaRequest: formik.values };
      objectRequest.empresaRequest.pessoa.conta.pessoaId = empresaAtual.pessoa.id;
      const empresaId = empresaAtual?.empresaId || '';
      try {
        await empresaOnboardingApi.atualizar(empresaId, 'CONTA_BANCARIA', objectRequest);
        setAtualizarEtapa(true);
      } catch (err) {
        apiUtils.handleApiError(err);
        setLoading(false);
      }
    }
  };

  const disabledColor = (campo, isDisabled) => {
    if (isDisabled) {
      return <span style={{ color: '#d3d3d3' }}> {campo} </span>;
    }
    return <span> {campo}</span>;
  };

  const campoAgencia = () => {
    let tamanhoMaximo = 10;
    if (isBancoBrasil || isBradesco) {
      tamanhoMaximo = 4;
    }

    return (
      <div>
        <div style={{ marginBottom: '7px' }}>{disabledColor('Agência *', desabilitarAgenciaConta)}</div>
        <TextInput
          id="pessoa.conta.contaBancaria.agencia"
          label=""
          pattern={onlyNumberRegex}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={_errors.pessoa?.conta?.contaBancaria?.agencia}
          touched={!!_errors.pessoa?.conta?.contaBancaria?.agencia}
          value={formik.values.pessoa?.conta?.contaBancaria?.agencia}
          disabled={desabilitarAgenciaConta}
          maxLength={tamanhoMaximo}
          placeholder="Número da agência"
          required
        />
      </div>
    );
  };

  const campoConta = () => {
    let tamanhoMaximo = 10;
    if (isBradesco) {
      tamanhoMaximo = 7;
    } else if (isBancoBrasil) {
      tamanhoMaximo = 8;
    }

    return (
      <div>
        <div style={{ marginBottom: '7px' }}>{disabledColor('Conta *', desabilitarAgenciaConta)}</div>
        <TextInput
          id="pessoa.conta.contaBancaria.numeroConta"
          label=""
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          pattern={onlyNumberRegex}
          value={formik.values.pessoa?.conta?.contaBancaria?.numeroConta}
          error={_errors.pessoa?.conta?.contaBancaria?.numeroConta}
          touched={!!_errors.pessoa?.conta?.contaBancaria.numeroConta}
          disabled={desabilitarAgenciaConta}
          maxLength={tamanhoMaximo}
          placeholder="Número da conta"
          required
        />
      </div>
    );
  };

  return (
    <OnboardingLayout
      onSubmit={handleSubmit}
      wizardStep={3}
      leftSidebarContent={
        <InformativoLateral titulo="Cobrança" subTitulo="Conta bancária" imagem="assets/images/onboarding/4.svg">
          Na sua conta bancária serão realizadas diversas transações, como geração de boletos bancários e emissão de
          relatórios gerenciais.
          <br />
          <br />
          <div style={{ fontWeight: 'bold', color: '#2e62b8' }}>Ainda não tem todos os dados?</div>
          Sem problema. Pule esta etapa e preencha depois.
        </InformativoLateral>
      }
      content={
        <>
          <Grid container>
            <Grid item lg={2} md={2} sm={2} xs={2} />
            <Grid item lg={8} md={8} sm={8} xs={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={8} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>
                    <span>Descrição *</span>
                    <span style={{ float: 'right' }}>
                      <Tooltip placement="top-end" title="Dê um nome à sua conta bancária">
                        <Help className="icone-informativo size-18" />
                      </Tooltip>
                    </span>
                  </div>
                  <TextInput
                    autoFocus
                    id="pessoa.conta.descricao"
                    label=""
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.pessoa?.conta?.descricao}
                    error={_errors.pessoa?.conta?.descricao}
                    touched={!!_errors.pessoa?.conta?.descricao}
                    maxLength={30}
                    placeholder="Descreva em poucas palavras"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>
                    <span>Tipo da conta *</span>
                  </div>
                  <SelectInput
                    id="pessoa.conta.contaBancaria.tipoConta"
                    label=""
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.pessoa?.conta?.contaBancaria?.tipoConta}
                    error={_errors.pessoa?.conta?.contaBancaria?.tipoConta}
                    touched={!!_errors.pessoa?.conta?.contaBancaria?.tipoConta}
                    options={[
                      { id: 'CORRENTE', title: 'Conta corrente' },
                      { id: 'POUPANCA', title: 'Conta poupança' },
                      { id: 'INVESTIMENTO', title: 'Conta investimento' },
                    ]}
                    placeholder="Selecione"
                    required
                    removeAsterisk
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12}>
                  <div style={{ marginBottom: '7px' }}>
                    <span>Banco *</span>
                  </div>
                  <SelectBanco
                    id="pessoa.conta.contaBancaria.banco"
                    name="pessoa.conta.contaBancaria.banco"
                    label=""
                    onChange={handleChangeBanco}
                    onBlur={formik.handleBlur}
                    value={formik.values.pessoa?.conta?.contaBancaria?.banco}
                    error={_errors.pessoa?.conta?.contaBancaria?.banco}
                    touched={!!_errors.pessoa?.conta?.contaBancaria?.banco}
                    required
                  />
                </Grid>
                <Grid item xs={8} sm={8} md={4} lg={4}>
                  {campoAgencia()}
                </Grid>
                <Grid item xs={4} sm={4} md={2} lg={2}>
                  <div style={{ marginBottom: '7px' }}>{disabledColor('Dígito ', desabilitarAgenciaConta)}</div>
                  <TextInput
                    id="pessoa.conta.contaBancaria.agenciaDigito"
                    label=""
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.pessoa?.conta?.contaBancaria?.agenciaDigito}
                    pattern={alphaNumericRegex}
                    maxLength={1}
                    disabled={desabilitarAgenciaConta}
                  />
                </Grid>
                <Grid item xs={8} sm={8} md={4} lg={4}>
                  {campoConta()}
                </Grid>
                <Grid item xs={4} sm={4} md={2} lg={2}>
                  <div style={{ marginBottom: '7px' }}>{disabledColor('Dígito *', desabilitarAgenciaConta)}</div>
                  <TextInput
                    id="pessoa.conta.contaBancaria.contaDigito"
                    label=""
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    pattern={alphaNumericRegex}
                    value={formik.values.pessoa?.conta?.contaBancaria?.contaDigito}
                    error={_errors.pessoa?.conta?.contaBancaria?.contaDigito}
                    touched={!!_errors.pessoa?.conta?.contaBancaria?.contaDigito}
                    disabled={desabilitarAgenciaConta}
                    maxLength={1}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>
                    <span>Saldo inicial (R$) *</span>
                    <span style={{ float: 'right' }}>
                      <Tooltip
                        placement="top-end"
                        title="Saldo que o sistema irá considerar para cálculos automáticos conforme movimentações posteriores à data informada."
                      >
                        <Help className="icone-informativo size-18" />
                      </Tooltip>
                    </span>
                  </div>
                  <CurrencyTextField
                    inputProps={{
                      style: { color: 'black' },
                    }}
                    id="pessoa.conta.contaBancaria.saldoInicial"
                    name="contaBancaria.saldoInicial"
                    label=""
                    outputFormat="number"
                    decimalCharacter=","
                    digitGroupSeparator="."
                    maximumValue="999999999.99"
                    decimalPlaces={2}
                    decimalPlacesShownOnBlur={2}
                    decimalPlacesShownOnFocus={2}
                    value={formik.values?.pessoa?.conta?.contaBancaria?.saldoInicial}
                    error={!!_errors.pessoa?.conta?.contaBancaria?.saldoInicial}
                    touched={!!_errors.pessoa?.conta?.contaBancaria?.saldoInicial}
                    helperText={_errors.pessoa?.conta?.contaBancaria?.saldoInicial || ''}
                    onChange={(e, value) => {
                      formik.setFieldValue('pessoa.conta.contaBancaria.saldoInicial', value);
                    }}
                    onBlur={formik.handleBlur}
                    placeholder="Saldo inicial da conta"
                    required
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6}>
                  <div style={{ marginBottom: '7px' }}>
                    <span>Data do saldo *</span>
                    <span style={{ float: 'right' }}>
                      <Tooltip
                        placement="top-end"
                        title="Data em que foi verificado o saldo informado no campo 'Saldo inicial'"
                      >
                        <Help className="icone-informativo size-18" />
                      </Tooltip>
                    </span>
                  </div>
                  <DateInput
                    fullWidth
                    disableFuture
                    id="pessoa.conta.contaBancaria.dataSaldo"
                    name="contaBancaria.dataSaldo"
                    variant="outlined"
                    labelWidth={0}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    helperText={_errors.pessoa?.conta?.contaBancaria?.dataSaldo}
                    value={formik.values.pessoa?.conta?.contaBancaria?.dataSaldo}
                    error={!!_errors.pessoa?.conta?.contaBancaria?.dataSaldo}
                    touched={!!_errors.pessoa?.conta?.contaBancaria?.dataSaldo}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item lg={2} md={2} sm={2} xs={2} />
          </Grid>
        </>
      }
    />
  );
};

export default ContaBancariaOnboarding;
